<script setup>
import SelectOffice from '@/components/shared/SelectOffice.vue'
import LoadingComponent from '@/components/shared/LoadingComponent.vue'
import ModalSelectAgreement from '@/components/agreement/ModalSelectAgreement.vue'
import BaseLayout from '@/components/layout/BaseLayout.vue'
import AdminLayout from '@/components/layout/admin/AdminLayout.vue'
import ChangePassword from '@/components/admin/users/ChangePassword.vue'
import NotificationsComponent from '@/components/shared/notifications/NotificationsComponent.vue'
import useInternetStatus from '@/helpers/internetStatus'
import { useVersionCheck } from '@/helpers/useVersionCheck'
import { useLoadingStore } from '@/stores/loading'
import { useUserStore } from '@/stores/user'
import { computed, watch, ref, onMounted, getCurrentInstance } from 'vue'
import { useRoute } from 'vue-router'
import { instance, factories } from '@/api'
import { useShoppingStore } from '@/stores/shopping'
import { useAuthStore } from '@/stores/auth'
import { useSiteConfigStore } from '@/stores/config/site'
import { useGeolocation } from '@/composables/useGeolocation'
import Swal from 'sweetalert2'

const selectOfficeRef = ref(null)
const { appContext } = getCurrentInstance()
const $googleMapsLoader = appContext.config.globalProperties.$googleMapsLoader
const { getCurrentPosition, getAddressFromPosition, getNearestOffice } =
  useGeolocation($googleMapsLoader)

// helper to check if browser has internet connection
useInternetStatus()

const environment = import.meta.env.VITE_ENVIRONMENT

const { timestampChanged, initVersionCheck, reloadApp } = useVersionCheck()
const authStoreData = useAuthStore()
const isLogged = computed(() => authStoreData.isLoggedIn)
const routeLabels = {
  signUp: 'Registro',
  signUpProspect: 'Registro de Prospectos',
  privacy_policy_public: 'Aviso de Privacidad Público',
  privacy_policy_public_2: 'Aviso de Privacidad Público 2',
  terms_and_conditions_public: 'Términos y Condiciones Público',
  terms_and_conditions_public_2: 'Términos y Condiciones Público 2',
  about_public: 'Acerca de Nosotros Público',
  contact_public: 'Contacto Público',
  job_board_public: 'Bolsa de Trabajo Público',
  not_found: 'Página No Encontrada',
  login: 'Acceso',
  loginPropects: 'Acceso Prospectos',
  home: 'Inicio',
  product_detail_public_pack_content: 'Detalle del Producto - Contenido Paquete Público',
  product_detail_public: 'Detalle del Producto Público',
  product_detail_only_list_pack_public: 'Detalle del Producto - Solo Lista Paquete Público',
  category_public: 'Categoría Pública',
  example_mail_order: 'Ejemplo de Correo de Orden',
  privacy_policy: 'Aviso de Privacidad',
  terms_and_conditions: 'Términos y Condiciones',
  productsByCategory: 'Productos por Categoría',
  productDetail: 'Detalle del Producto',
  searchProducts: 'Buscar Productos',
  shoppingCart: 'Carrito de Compras',
  about: 'Acerca de Nosotros',
  branches: 'Sucursales',
  contact: 'Contacto',
  orderDetail: 'Detalle de Orden',
  backorder: 'Backorder',
  myAccount: 'Mi Cuenta',
  productsByBrand: 'Productos por Marca',
  'admin.users': 'Administración de Usuarios',
  'admin.client_account': 'Cuenta del Cliente',
  'admin.product_pdf': 'PDF de Producto',
  'admin.generate_pdf': 'Generador de PDF',
  'admin.clients': 'Clientes',
  'admin.orders_list': 'Lista de Órdenes',
  'admin.agreement_orders_list': 'Lista de Órdenes de Convenio',
  'admin.quoations_list': 'Lista de Cotizaciones',
  'admin.reports': 'Informes',
  'admin.client_active': 'Clientes Activos',
  'admin.order_detail': 'Detalle de la Orden',
  'admin.orders': 'Órdenes',
  'admin.products_list': 'Lista de Productos',
  'admin.agreement_products_list': 'Lista de Productos de Convenio',
  'admin.categories_list': 'Lista de Categorías',
  'admin.configuration': 'Configuración'
}

const api = factories(instance, ['user', 'office'])

const route = useRoute()

const actionsUsersAppHome = ref(null)
const modalSelectAgreement = ref(null)

const isStoreView = computed(() => {
  const publicRoutes = [
    'example_mail_order',
    'privacy_policy_public',
    'product_detail_public_pack_content',
    'terms_and_conditions_public',
    'signUp',
    'signUpProspect',
    'loginPropects',
    'not_found',
    'admin.product_pdf'
  ]
  return !publicRoutes.includes(route?.name) && !route?.name?.includes('admin')
})

const isAdminView = computed(() => {
  return route?.name?.includes('admin') && route?.name !== 'admin.product_pdf'
})

const loadingStore = useLoadingStore()
const shoppingStore = useShoppingStore()
const userStore = useUserStore()
const siteConfigStore = useSiteConfigStore()
const userLocationLogic = siteConfigStore.getUserLocationLogic
const brandName = computed(() => siteConfigStore.getBrandName)
const baseTitle = computed(() => siteConfigStore.getBaseTitle)
const agreementLoginShowInfo = computed(() => userStore.getAgrementLoginShowInfo)
const isClient = computed(() => userStore.isClient)
const getBalanceLogicState = computed(() => userStore.getIsAgreementActive)
const requireChangePassword = computed(() => userStore.requireChangePassword)
const userBalance = computed({
  get: () => shoppingStore.getUserBalance,
  set: (newValue) => {
    shoppingStore.updateUserBalance(newValue)
  }
})

const updateProfile = () => {
  api.user.profile().then((response) => {
    userBalance.value = response?.data?.balance || 0
    userStore.updateUserInfo(response, false)
  })
}

function updateFavicon(brandName) {
  const sizes = ['16x16', '32x32']
  sizes.forEach((size) => {
    const link = document.querySelector(`link[rel='icon'][sizes='${size}']`)
    if (link) {
      link.href = `/favicons/${brandName.toLowerCase()}-${size}.png`
    }
  })
}

watch(
  brandName,
  (newBrandName) => {
    updateFavicon(newBrandName)
  },
  { immediate: true }
)

watch(userStore, (newVal) => {
  if (newVal?.userInfo?.change_password) {
    actionsUsersAppHome?.value?.showModal()
  }
})

watch(
  () => route.name,
  (newRouteName) => {
    userStore.updateCurrentRoute(newRouteName)
    const title = routeLabels[newRouteName] || 'Bienvenido'
    document.title = `${baseTitle.value} - ${title}`
    if (requireChangePassword.value) {
      actionsUsersAppHome.value?.showModal()
      return
    }
    if (
      isLogged.value &&
      !agreementLoginShowInfo.value &&
      getBalanceLogicState.value &&
      isClient.value
    ) {
      modalSelectAgreement?.value?.showModal()
    }
  },
  { immediate: true }
)

async function getLocationAndSaveToUserStore() {
  try {
    const pos = await getCurrentPosition()
    const address = await getAddressFromPosition(pos)
    const nearestOffice = await getNearestOffice(pos, userStore.offices)
    userStore.setOfficeSelected(nearestOffice)
    userStore.updateUserLocation({
      location: pos,
      ...address
    })
  } catch (e) {
    // Maneja el error si lo necesitas
    console.error(e)
    userStore.setOfficeSelected(userStore.getOfficesList[0])
  }
}

const getOfficesLocations = () => {
  if (userStore.listOfficesHasData) return
  api.office.list().then((response) => {
    userStore.updateOfficeList(response.data.offices)
    userStore.setOfficeSelected(userStore.getOfficesList[0])
  })
}

async function checkGeolocationPermission() {
  if (!navigator.permissions) return null // No soportado
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' })
    return status.state // 'granted', 'prompt', o 'denied'
  } catch (e) {
    return null
  }
}

// Ejemplo de uso en askPermissionForLocation:
const askPermissionForLocation = async () => {
  const permission = await checkGeolocationPermission()
  if (permission === 'granted') {
    // Ya tienes permiso, puedes obtener la ubicación directamente
    getLocationAndSaveToUserStore()
    return
  }
  if (!userStore.hasSeenLocationPrompt) {
    Swal.fire({
      title: '¡Queremos mejorar tu experiencia!',
      text: 'Necesitamos tu ubicación para mostrarte la sucursal más cercana. ¿Nos das permiso?',
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Sí, usar mi ubicación',
      cancelButtonText: 'No, gracias'
    }).then((result) => {
      userStore.setHasSeenLocationPrompt(true)
      if (result.isConfirmed) {
        // Aquí llamas a tu función para obtener la ubicación y buscar la sucursal más cercana
        getLocationAndSaveToUserStore()
      } else {
        // Aquí seleccionas la primera sucursal por defecto
        userStore.setOfficeSelected(userStore.offices[0])
      }
    })
  } else if (!userStore.getOfficeSelected) {
    // Si no hay una sucursal seleccionada, seleccionamos la primera por defecto
    userStore.setOfficeSelected(userStore.offices[0])
  }
}

watch(
  () => userStore.getModalSelectOffice,
  (modalLoad) => {
    if (modalLoad) {
      userStore.setModalSelectOffice(selectOfficeRef.value)
    }
  },
  { immediate: true }
)

onMounted(async () => {
  userStore.setModalSelectOffice(selectOfficeRef.value)
  if (userLocationLogic) {
    await getOfficesLocations()
    askPermissionForLocation()
  }
  if (requireChangePassword.value) {
    actionsUsersAppHome.value?.showModal()
    return
  }
  if (isLogged.value) {
    updateProfile()
    if (!agreementLoginShowInfo.value && getBalanceLogicState.value && isClient.value) {
      modalSelectAgreement?.value?.showModal()
    }
  }
  if (environment !== 'development') {
    initVersionCheck(`${window.location.origin}/version.json`)
  }

  if (timestampChanged.value) {
    Swal.fire({
      title: 'Actualización disponible',
      text: 'Una nueva versión está disponible. Por favor, recarga la página.',
      icon: 'info',
      showCancelButton: false,
      confirmButtonText: 'Recargar'
    }).then((result) => {
      if (result.isConfirmed) {
        reloadApp()
      }
    })
  }
})
</script>

<template>
  <SelectOffice ref="selectOfficeRef" />
  <ModalSelectAgreement ref="modalSelectAgreement" />
  <NotificationsComponent />
  <LoadingComponent v-if="loadingStore.getLoaderCount > 0" />
  <template v-if="isStoreView">
    <BaseLayout />
  </template>
  <template v-else-if="isAdminView">
    <AdminLayout />
  </template>
  <template v-else>
    <router-view />
  </template>
  <ChangePassword ref="actionsUsersAppHome" />
  <!-- <ConfirmationModal /> -->
</template>

<style lang="scss">
@import '@/assets/scss/main.scss';
</style>
